import { useState } from 'react';
import {
  Plus,
  Trash2,
  Edit2,
  MoreHorizontal,
  Settings2,
  GripVertical,
  Check,
  X,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { useModifierGroups, type ModifierGroup, type ModifierOption } from '@/hooks/useModifiers';
import { useCurrency } from '@/hooks/useCurrency';
import { cn } from '@/lib/utils';

export default function Modifiers() {
  const {
    modifierGroups,
    isLoading,
    createModifierGroup,
    updateModifierGroup,
    deleteModifierGroup,
    addModifierOption,
    updateModifierOption,
    deleteModifierOption,
  } = useModifierGroups();
  const { formatCurrency } = useCurrency();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ModifierGroup | null>(null);
  const [editingOption, setEditingOption] = useState<{ groupId: string; option: ModifierOption } | null>(null);
  const [addingOptionToGroup, setAddingOptionToGroup] = useState<string | null>(null);

  // Form state for new group
  const [newGroupForm, setNewGroupForm] = useState({
    name: '',
    minSelections: 0,
    maxSelections: 1,
    isRequired: false,
    options: [{ name: '', priceAdjustment: 0, isDefault: false }],
  });

  // Form state for new option
  const [newOptionForm, setNewOptionForm] = useState({
    name: '',
    priceAdjustment: 0,
    isDefault: false,
  });

  const resetNewGroupForm = () => {
    setNewGroupForm({
      name: '',
      minSelections: 0,
      maxSelections: 1,
      isRequired: false,
      options: [{ name: '', priceAdjustment: 0, isDefault: false }],
    });
  };

  const handleCreateGroup = async () => {
    if (!newGroupForm.name.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    const validOptions = newGroupForm.options.filter(opt => opt.name.trim());
    
    try {
      await createModifierGroup.mutateAsync({
        name: newGroupForm.name.trim(),
        minSelections: newGroupForm.minSelections,
        maxSelections: newGroupForm.maxSelections,
        isRequired: newGroupForm.isRequired,
        options: validOptions,
      });
      toast.success('Modifier group created');
      setCreateDialogOpen(false);
      resetNewGroupForm();
    } catch (error) {
      toast.error('Failed to create modifier group');
    }
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup) return;

    try {
      await updateModifierGroup.mutateAsync({
        id: editingGroup.id,
        name: editingGroup.name,
        minSelections: editingGroup.min_selections,
        maxSelections: editingGroup.max_selections,
        isRequired: editingGroup.is_required,
      });
      toast.success('Modifier group updated');
      setEditingGroup(null);
    } catch (error) {
      toast.error('Failed to update modifier group');
    }
  };

  const handleDeleteGroup = async (id: string) => {
    if (!confirm('Are you sure you want to delete this modifier group? This will remove it from all products.')) {
      return;
    }

    try {
      await deleteModifierGroup.mutateAsync(id);
      toast.success('Modifier group deleted');
    } catch (error) {
      toast.error('Failed to delete modifier group');
    }
  };

  const handleAddOption = async (groupId: string) => {
    if (!newOptionForm.name.trim()) {
      toast.error('Please enter an option name');
      return;
    }

    try {
      await addModifierOption.mutateAsync({
        groupId,
        name: newOptionForm.name.trim(),
        priceAdjustment: newOptionForm.priceAdjustment,
        isDefault: newOptionForm.isDefault,
      });
      toast.success('Option added');
      setAddingOptionToGroup(null);
      setNewOptionForm({ name: '', priceAdjustment: 0, isDefault: false });
    } catch (error) {
      toast.error('Failed to add option');
    }
  };

  const handleUpdateOption = async () => {
    if (!editingOption) return;

    try {
      await updateModifierOption.mutateAsync({
        id: editingOption.option.id,
        name: editingOption.option.name,
        priceAdjustment: editingOption.option.price_adjustment,
        isDefault: editingOption.option.is_default,
        isActive: editingOption.option.is_active,
      });
      toast.success('Option updated');
      setEditingOption(null);
    } catch (error) {
      toast.error('Failed to update option');
    }
  };

  const handleDeleteOption = async (optionId: string) => {
    try {
      await deleteModifierOption.mutateAsync(optionId);
      toast.success('Option deleted');
    } catch (error) {
      toast.error('Failed to delete option');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Modifier Groups</h2>
          <p className="text-sm text-muted-foreground">
            Create modifier groups to add customization options to your products
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Modifier Group
        </Button>
      </div>

      {/* Modifier Groups List */}
      {modifierGroups.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Settings2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No modifier groups yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create modifier groups like "Size", "Toppings", or "Add-ons" to customize your products
            </p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Your First Modifier Group
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {modifierGroups.map((group) => (
            <Card key={group.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      {group.name}
                      {group.is_required && (
                        <Badge variant="destructive" className="text-[10px]">Required</Badge>
                      )}
                    </CardTitle>
                    <CardDescription className="text-xs mt-1">
                      {group.min_selections > 0 && `Min: ${group.min_selections}`}
                      {group.min_selections > 0 && group.max_selections > 0 && ' • '}
                      {group.max_selections > 0 && `Max: ${group.max_selections}`}
                      {!group.min_selections && !group.max_selections && 'No selection limits'}
                    </CardDescription>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditingGroup(group)}>
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit Group
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => handleDeleteGroup(group.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Group
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                {/* Options List */}
                <div className="space-y-2">
                  {group.options?.map((option) => (
                    <div
                      key={option.id}
                      className={cn(
                        "flex items-center justify-between p-2 rounded-md border bg-muted/30",
                        !option.is_active && "opacity-50"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                        <div>
                          <span className="font-medium text-sm">{option.name}</span>
                          {option.is_default && (
                            <Badge variant="secondary" className="ml-2 text-[10px]">Default</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          "text-sm tabular-nums",
                          option.price_adjustment > 0 && "text-green-600",
                          option.price_adjustment < 0 && "text-red-600"
                        )}>
                          {option.price_adjustment > 0 && '+'}
                          {formatCurrency(option.price_adjustment)}
                        </span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <MoreHorizontal className="h-3 w-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditingOption({ groupId: group.id, option })}>
                              <Edit2 className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => handleDeleteOption(option.id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}

                  {/* Add Option Button/Form */}
                  {addingOptionToGroup === group.id ? (
                    <div className="flex items-center gap-2 p-2 rounded-md border border-dashed">
                      <Input
                        placeholder="Option name"
                        value={newOptionForm.name}
                        onChange={(e) => setNewOptionForm({ ...newOptionForm, name: e.target.value })}
                        className="h-8"
                        autoFocus
                      />
                      <Input
                        type="number"
                        placeholder="Price"
                        value={newOptionForm.priceAdjustment || ''}
                        onChange={(e) => setNewOptionForm({ ...newOptionForm, priceAdjustment: parseFloat(e.target.value) || 0 })}
                        className="h-8 w-24"
                      />
                      <Button size="sm" onClick={() => handleAddOption(group.id)}>
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setAddingOptionToGroup(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full border border-dashed"
                      onClick={() => setAddingOptionToGroup(group.id)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Option
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Group Dialog */}
      <ResponsiveModal open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <ResponsiveModalContent>
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Create Modifier Group</ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Create a new modifier group with options that customers can choose from
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>
          <ResponsiveModalBody className="space-y-4">
            <div className="space-y-2">
              <Label>Group Name *</Label>
              <Input
                placeholder="e.g., Size, Toppings, Add-ons"
                value={newGroupForm.name}
                onChange={(e) => setNewGroupForm({ ...newGroupForm, name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Minimum Selections</Label>
                <Input
                  type="number"
                  min="0"
                  value={newGroupForm.minSelections}
                  onChange={(e) => setNewGroupForm({ ...newGroupForm, minSelections: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Maximum Selections</Label>
                <Input
                  type="number"
                  min="1"
                  value={newGroupForm.maxSelections}
                  onChange={(e) => setNewGroupForm({ ...newGroupForm, maxSelections: parseInt(e.target.value) || 1 })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label>Required</Label>
              <Switch
                checked={newGroupForm.isRequired}
                onCheckedChange={(checked) => setNewGroupForm({ ...newGroupForm, isRequired: checked })}
              />
            </div>

            <div className="space-y-2">
              <Label>Options</Label>
              {newGroupForm.options.map((opt, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    placeholder="Option name"
                    value={opt.name}
                    onChange={(e) => {
                      const newOptions = [...newGroupForm.options];
                      newOptions[index].name = e.target.value;
                      setNewGroupForm({ ...newGroupForm, options: newOptions });
                    }}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    placeholder="Price"
                    value={opt.priceAdjustment || ''}
                    onChange={(e) => {
                      const newOptions = [...newGroupForm.options];
                      newOptions[index].priceAdjustment = parseFloat(e.target.value) || 0;
                      setNewGroupForm({ ...newGroupForm, options: newOptions });
                    }}
                    className="w-24"
                  />
                  {newGroupForm.options.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const newOptions = newGroupForm.options.filter((_, i) => i !== index);
                        setNewGroupForm({ ...newGroupForm, options: newOptions });
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setNewGroupForm({
                  ...newGroupForm,
                  options: [...newGroupForm.options, { name: '', priceAdjustment: 0, isDefault: false }]
                })}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Option
              </Button>
            </div>
          </ResponsiveModalBody>
          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateGroup} disabled={createModifierGroup.isPending}>
              {createModifierGroup.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Create Group
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Edit Group Dialog */}
      <ResponsiveModal open={!!editingGroup} onOpenChange={() => setEditingGroup(null)}>
        <ResponsiveModalContent>
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Edit Modifier Group</ResponsiveModalTitle>
          </ResponsiveModalHeader>
          <ResponsiveModalBody className="space-y-4">
            {editingGroup && (
              <>
                <div className="space-y-2">
                  <Label>Group Name</Label>
                  <Input
                    value={editingGroup.name}
                    onChange={(e) => setEditingGroup({ ...editingGroup, name: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Min Selections</Label>
                    <Input
                      type="number"
                      min="0"
                      value={editingGroup.min_selections}
                      onChange={(e) => setEditingGroup({ ...editingGroup, min_selections: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Selections</Label>
                    <Input
                      type="number"
                      min="1"
                      value={editingGroup.max_selections}
                      onChange={(e) => setEditingGroup({ ...editingGroup, max_selections: parseInt(e.target.value) || 1 })}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Label>Required</Label>
                  <Switch
                    checked={editingGroup.is_required}
                    onCheckedChange={(checked) => setEditingGroup({ ...editingGroup, is_required: checked })}
                  />
                </div>
              </>
            )}
          </ResponsiveModalBody>
          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setEditingGroup(null)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateGroup} disabled={updateModifierGroup.isPending}>
              {updateModifierGroup.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Edit Option Dialog */}
      <ResponsiveModal open={!!editingOption} onOpenChange={() => setEditingOption(null)}>
        <ResponsiveModalContent>
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>Edit Option</ResponsiveModalTitle>
          </ResponsiveModalHeader>
          <ResponsiveModalBody className="space-y-4">
            {editingOption && (
              <>
                <div className="space-y-2">
                  <Label>Option Name</Label>
                  <Input
                    value={editingOption.option.name}
                    onChange={(e) => setEditingOption({
                      ...editingOption,
                      option: { ...editingOption.option, name: e.target.value }
                    })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Price Adjustment</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingOption.option.price_adjustment}
                    onChange={(e) => setEditingOption({
                      ...editingOption,
                      option: { ...editingOption.option, price_adjustment: parseFloat(e.target.value) || 0 }
                    })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Default Selection</Label>
                  <Switch
                    checked={editingOption.option.is_default}
                    onCheckedChange={(checked) => setEditingOption({
                      ...editingOption,
                      option: { ...editingOption.option, is_default: checked }
                    })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Active</Label>
                  <Switch
                    checked={editingOption.option.is_active}
                    onCheckedChange={(checked) => setEditingOption({
                      ...editingOption,
                      option: { ...editingOption.option, is_active: checked }
                    })}
                  />
                </div>
              </>
            )}
          </ResponsiveModalBody>
          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setEditingOption(null)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateOption} disabled={updateModifierOption.isPending}>
              {updateModifierOption.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </div>
  );
}
